/**
 * Bundel Klassiek — boxershort bundle builder
 * Implements the "Bundel Klassiek.dc.html" design as a working theme block.
 *
 * Flow: Aantal → Kleur → Maat → In winkelmand.
 * - Color/size come from product variants (auto-sync).
 * - Pack quantity = how many units are added to the cart.
 * - "Kleurmix" evenly distributes the pack across all colours (disabled at 1-pack).
 * - Optional "gratis-boxers modus": tiers become "X+Y gratis" (pay X at anchor price,
 *   get Y free), progress nudges in free-boxer language, total savings in money below.
 * - Shown prices are theme settings; the actual discount is applied by the store's
 *   discount app / Shopify discount (incl. buy-X-get-Y-free for the free mode).
 */

class BundelKlassiek extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    try {
      this.config = JSON.parse(this.querySelector('[data-config]')?.textContent || '{}');
    } catch (_) {
      this.config = {};
    }
    try {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch (_) {
      this.variants = [];
    }

    this.anchor = parseFloat(this.dataset.anchor) || 0;
    this.recommendedQty = parseInt(this.dataset.recommendedQty, 10) || 0;
    this.mixEnabled = this.dataset.enableMix === 'true';
    this.mixLabel = this.dataset.mixLabel || 'Kleurmix';

    // Editable options / formatting.
    this.showRecommended = this.dataset.showRecommended !== 'false';
    this.autoCurrency = this.dataset.autoCurrency === 'true';
    this.priceRounding = this.dataset.priceRounding || 'none';
    this.currency = this.dataset.currency || '';
    this.savingsDisplay = this.dataset.savingsDisplay || 'amount_pct';
    this.savingsPrefix = this.dataset.savingsPrefix || 'Bespaar';
    this.perSuffix = this.dataset.perSuffix || 'p/s';
    this.singleLabel = this.dataset.singleLabel || '1 Boxer';
    this.pluralLabel = this.dataset.pluralLabel || 'Boxers';
    this.progressTemplate = this.dataset.progressTemplate || 'Voeg +[aantal] [eenheid] toe → [prijs] p/s';
    this.progressUnit = this.dataset.progressUnit || 'boxer';
    this.progressUnitPlural = this.dataset.progressUnitPlural || 'boxers';
    this.progressDone = this.dataset.progressDone || 'Laagste prijs per stuk bereikt';
    this.progressCurrentTemplate = this.dataset.progressCurrentTemplate || '';
    this.progressCurrency = this.dataset.progressCurrency || '';

    // Gratis-boxers modus.
    this.freeMode = this.dataset.freeMode === 'true';
    this.freeLabelTemplate = this.dataset.freeLabelTemplate || '[betaald]+[gratis] gratis';
    this.freeProgressTemplate = this.dataset.freeProgressTemplate || 'Voeg nog [aantal] toe voor nog [gratis] gratis boxers';
    this.freeProgressCurrent = this.dataset.freeProgressCurrent || 'Totale besparing: [bedrag]';
    this.freeDone = this.dataset.freeDone || 'Je hebt het maximale aantal gratis boxers!';
    this.freeShowPer = this.dataset.freeShowPer !== 'false';
    this.freeShowSavings = this.dataset.freeShowSavings !== 'false';
    this.freeRecommended = this.dataset.freeRecommended || '';

    // Deal-timer.
    this.timerShow = this.dataset.timerShow === 'true';
    this.timerMode = this.dataset.timerMode || 'daily';
    this.timerDate = this.dataset.timerDate || '';
    this.timerTime = this.dataset.timerTime || '';
    this.timerLabels = (this.dataset.timerLabels || 'dagen,uur,min,sec').split(',').map((s) => s.trim());
    this.timerExpired = this.dataset.timerExpired || '';

    this.realColors = (this.config.colors || []).map((c) => c.value);
    this.colorPosition = this.config.colorPosition || 1;
    this.sizePosition = this.config.sizePosition || 2;

    // Collect tier buttons. Each carries both the normal (qty/price) and the
    // free-mode (paid/free) config + an optional custom label.
    var all = [...this.querySelectorAll('[data-tier]')].map((el) => ({
      el,
      qty: parseInt(el.dataset.qty, 10) || 0,
      price: parseFloat(el.dataset.price) || 0,
      perPrice: parseFloat(el.dataset.perPrice) || 0,
      paid: parseInt(el.dataset.paid, 10) || 0,
      free: parseInt(el.dataset.free, 10) || 0,
      label: el.dataset.label || '',
      freePrice: parseFloat(el.dataset.freePrice) || 0,
      freeLabel: el.dataset.freeLabel || '',
      freeProgress: el.dataset.freeProgress || '',
      slot: el.dataset.slot || '',
    }));
    // Show/hide per active mode, keep only the visible ones, sort by total.
    all.forEach((t) => { t.el.style.display = this.tierView(t).visible ? '' : 'none'; });
    this.tierEls = all
      .filter((t) => this.tierView(t).visible)
      .sort((a, b) => this.tierView(a).total - this.tierView(b).total);

    // Initial selection. Normale modus: op recommended_qty. Gratis-modus: eigen voorselectie (slot).
    let di;
    if (this.freeMode) {
      di = this.tierEls.findIndex((t) => t.slot === this.freeRecommended);
    } else {
      const defaultQty = parseInt(this.dataset.defaultQty, 10);
      di = this.tierEls.findIndex((t) => t.qty === defaultQty);
    }
    if (di < 0) di = 0;
    this.state = {
      tierIndex: di,
      color: this.realColors[0] || null,
      mix: false,
      size: this.firstSize(),
    };

    this.applyMixGradient();
    this.bindEvents();
    this.render();
    this.startTimer();
  }

  /* ---------- deal-timer ---------- */
  startTimer() {
    const el = this.querySelector('[data-timer]');
    if (!el || !this.timerShow) return;
    this.timerEl = el;
    this.timerClockEl = el.querySelector('[data-timer-clock]');
    this.timerTitleEl = el.querySelector('[data-timer-title]');

    if (this.timerMode === 'fixed') {
      this.timerTarget = this.parseFixedTarget();
      // Geen/ongeldige datum → timer verbergen i.p.v. iets fouts tonen.
      if (this.timerTarget == null) { el.hidden = true; return; }
    }

    el.hidden = false;
    this.tickTimer();
    this._timerInt = setInterval(() => this.tickTimer(), 1000);
  }

  // Vaste einddatum → timestamp in de LOKALE tijd van de bezoeker.
  parseFixedTarget() {
    const m = (this.timerDate || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!m) return null;
    let hh = 0, mm = 0;
    const tm = (this.timerTime || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (tm) { hh = parseInt(tm[1], 10); mm = parseInt(tm[2], 10); }
    const dt = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10), hh, mm, 0, 0);
    const ms = dt.getTime();
    return isNaN(ms) ? null : ms;
  }

  tickTimer() {
    const now = Date.now();
    let remaining;
    if (this.timerMode === 'fixed') {
      remaining = this.timerTarget - now;
      if (remaining <= 0) { this.expireTimer(); return; }
    } else {
      // Dagelijks: tijd tot de eerstvolgende middernacht in de tijdzone van de bezoeker.
      // Elke tick opnieuw berekend → na 00:00 telt 'ie automatisch af naar de volgende dag.
      const d = new Date();
      const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
      remaining = next.getTime() - now;
    }
    this.renderTimer(remaining);
  }

  renderTimer(remaining) {
    if (!this.timerClockEl) return;
    const totalSec = Math.max(0, Math.floor(remaining / 1000));
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const L = this.timerLabels;
    const pad = (n) => String(n).padStart(2, '0');
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const segs = [];
    if (days > 0) segs.push([days, L[0] || 'dagen']);
    segs.push([hours, L[1] || 'uur']);
    segs.push([mins, L[2] || 'min']);
    segs.push([secs, L[3] || 'sec']);

    this.timerClockEl.innerHTML = segs
      .map((s, i) => {
        const sep = i < segs.length - 1 ? '<span class="bk__timer-colon">:</span>' : '';
        return '<span class="bk__timer-seg"><span class="bk__timer-num">' + pad(s[0]) +
          '</span><span class="bk__timer-unit">' + esc(s[1]) + '</span></span>' + sep;
      })
      .join('');
  }

  expireTimer() {
    if (this._timerInt) clearInterval(this._timerInt);
    if (!this.timerEl) return;
    // Geen afloop-tekst → timer helemaal verbergen. Anders titel vervangen, klok verbergen.
    if (!this.timerExpired) { this.timerEl.hidden = true; return; }
    this.timerEl.classList.add('bk__timer--expired');
    if (this.timerTitleEl) this.timerTitleEl.textContent = this.timerExpired;
  }

  // Active display values for a tier, depending on the mode.
  tierView(t) {
    // Defensief: zonder (zichtbare) staffel is er geen tier-object. Geef een lege,
    // veilige view terug i.p.v. te crashen op t.qty / t.paid (voorkomt de
    // "cannot read properties of undefined (reading 'qty')"-fout).
    if (!t) {
      return { total: 0, price: 0, per: 0, full: 0, save: 0, free: 0, paid: 0, visible: false, label: '' };
    }
    if (this.freeMode) {
      const total = t.paid + t.free;
      const full = total * this.anchor;
      // Prijs: eigen gratis-modus prijs als ingevuld, anders betaald × ankerprijs.
      const price = t.freePrice > 0 ? t.freePrice : t.paid * this.anchor;
      // Label: eigen tekst > "X+Y gratis" (bij gratis>0) > "1 Boxer"-stijl (1-pack zonder gratis).
      let label;
      if (t.freeLabel !== '') {
        label = t.freeLabel;
      } else if (t.free > 0) {
        label = this.fillTokens(this.freeLabelTemplate, { betaald: t.paid, gratis: t.free });
      } else {
        label = t.paid === 1 ? this.singleLabel : t.paid + ' ' + this.pluralLabel;
      }
      return {
        total: total,
        price: price,
        // Prijs per stuk: eigen override als ingevuld, anders automatisch (prijs ÷ totaal incl. gratis).
        per: t.perPrice > 0 ? t.perPrice : (total > 0 ? price / total : 0),
        full: full,
        save: full - price,
        free: t.free,
        paid: t.paid,
        visible: t.paid > 0,
        label: label,
      };
    }
    const full = this.anchor * t.qty;
    return {
      total: t.qty,
      price: t.price,
      // Prijs per stuk: eigen override als ingevuld, anders automatisch (prijs ÷ aantal).
      per: t.perPrice > 0 ? t.perPrice : (t.qty > 0 ? t.price / t.qty : 0),
      full: full,
      save: full - t.price,
      visible: t.qty > 0,
      label: t.label !== '' ? t.label : (t.qty === 1 ? this.singleLabel : t.qty + ' ' + this.pluralLabel),
    };
  }

  applyMixGradient() {
    const mixSwatch = this.querySelector('[data-color][data-mix="true"] [data-swatch]');
    if (!mixSwatch) return;
    const hexes = (this.config.colors || []).map((c) => c.hex).filter(Boolean);
    if (hexes.length < 2) return;
    const step = 100 / hexes.length;
    const stops = hexes
      .map((hex, i) => `${hex} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`)
      .join(', ');
    mixSwatch.style.background = `conic-gradient(from 215deg, ${stops})`;
  }

  /* ---------- helpers ---------- */
  // Rond een (al omgerekend) bedrag af volgens de gekozen instelling.
  // 'ending_X' snapt naar het dichtstbijzijnde heel getal + X (drempel op .50).
  roundAuto(value) {
    switch (this.priceRounding) {
      case 'integer': return Math.round(value);
      case 'ending_99': return Math.round(value - 0.99) + 0.99;
      case 'ending_95': return Math.round(value - 0.95) + 0.95;
      case 'ending_50': return Math.round(value - 0.5) + 0.5;
      default: return value;
    }
  }

  moneyAuto(n) {
    const c = (window.Shopify && window.Shopify.currency) || {};
    const rate = parseFloat(c.rate) || 1;
    const active = c.active || 'EUR';
    const locale = (window.Shopify && window.Shopify.locale) || undefined;
    const value = this.roundAuto(Number(n) * rate);
    // Bij afronden op heel getal geen decimalen tonen ($22 i.p.v. $22,00).
    const noDecimals = this.priceRounding === 'integer';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: active,
        minimumFractionDigits: noDecimals ? 0 : 2,
        maximumFractionDigits: noDecimals ? 0 : 2,
      }).format(value);
    } catch (_) {
      return noDecimals ? String(Math.round(value)) : value.toFixed(2).replace('.', ',');
    }
  }

  fmt(n) {
    if (this.autoCurrency) return this.moneyAuto(n);
    return this.currency + Number(n).toFixed(2).replace('.', ',');
  }

  fmtProgress(n) {
    if (this.autoCurrency) return this.moneyAuto(n);
    return this.progressCurrency + Number(n).toFixed(2).replace('.', ',');
  }

  savingsLine(amount, pct) {
    const prefix = (this.savingsPrefix + ' ').trimStart();
    switch (this.savingsDisplay) {
      case 'none':
        return '';
      case 'amount':
        return (prefix + this.fmt(amount)).trim();
      case 'pct':
        return '−' + pct + '%';
      default:
        return (prefix + this.fmt(amount)).trim() + ' · −' + pct + '%';
    }
  }

  firstSize() {
    const first = this.querySelector('[data-size]');
    return first ? first.dataset.value : null;
  }

  currentTier() {
    return this.tierEls[this.state.tierIndex] || this.tierEls[0];
  }

  currentTotal() {
    var t = this.currentTier();
    return t ? this.tierView(t).total : 0;
  }

  colorLabel() {
    return this.state.mix ? this.mixLabel : this.state.color || '';
  }

  /* ---------- events ---------- */
  bindEvents() {
    this.querySelector('[data-tiers]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tier]');
      if (!btn) return;
      const idx = this.tierEls.findIndex((t) => t.el === btn);
      if (idx < 0) return;
      this.state.tierIndex = idx;
      // Kleurmix kan niet bij 1 stuk — val terug op de eerste kleur.
      if (this.currentTotal() === 1 && this.state.mix) {
        this.state.mix = false;
        this.state.color = this.realColors[0] || null;
      }
      this.render();
    });

    this.querySelector('[data-colors]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-color]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      if (btn.dataset.mix === 'true') {
        this.state.mix = true;
      } else {
        this.state.mix = false;
        this.state.color = btn.dataset.value;
      }
      this.render();
    });

    this.querySelector('[data-sizes]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size]');
      if (!btn) return;
      this.state.size = btn.dataset.value;
      this.render();
    });

    this.querySelector('[data-add]')?.addEventListener('click', () => this.addToCart());

    this.querySelector('[data-open-guide]')?.addEventListener('click', () => this.toggleGuide(true));
    this.querySelectorAll('[data-guide-close]').forEach((el) =>
      el.addEventListener('click', () => this.toggleGuide(false))
    );
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.toggleGuide(false);
    });
  }

  toggleGuide(open) {
    const modal = this.querySelector('[data-guide]');
    if (!modal) return;
    modal.hidden = !open;
    document.documentElement.style.overflow = open ? 'hidden' : '';
  }

  /* ---------- token-vervanging (taal-agnostisch) ---------- */
  // Vervangt [token] én bekende vertalingen door de waarde. Zo blijven de
  // sjablonen werken nadat een vertaal-app de tokens heeft meevertaald
  // (bv. [bedrag] → [amount], [aantal] → [number], [gratis] → [free]).
  fillTokens(tpl, values) {
    // NL = EN = ES = DE = FR. Vertaal-apps vertalen de tokenwoorden mee,
    // dus we accepteren de gangbare vertalingen per token.
    const aliases = {
      aantal: ['aantal', 'number', 'quantity', 'qty', 'count', 'cantidad', 'cantidades', 'numero', 'número', 'anzahl', 'menge', 'nombre', 'quantite', 'quantité'],
      bedrag: ['bedrag', 'amount', 'sum', 'value', 'importe', 'monto', 'cantidad', 'betrag', 'montant'],
      eenheid: ['eenheid', 'unit', 'unidad', 'unidades', 'einheit', 'unite', 'unité'],
      besparing: ['besparing', 'savings', 'saving', 'ahorro', 'ersparnis', 'economie', 'économie', 'economies', 'économies'],
      korting: ['korting', 'discount', 'descuento', 'rabatt', 'remise', 'reduccion', 'reducción', 'reduction', 'réduction'],
      prijs: ['prijs', 'price', 'precio', 'preis', 'prix'],
      gratis: ['gratis', 'free', 'gratuito', 'gratuitos', 'gratuit', 'gratuits', 'kostenlos'],
      betaald: ['betaald', 'paid', 'pagado', 'pagados', 'bezahlt', 'paye', 'payé', 'payes', 'payés'],
    };
    let out = String(tpl == null ? '' : tpl);
    for (const key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
      const val = values[key];
      if (val == null) continue;
      const names = aliases[key] || [key];
      for (let i = 0; i < names.length; i++) {
        out = out.split('[' + names[i] + ']').join(val);
      }
    }
    return out;
  }

  /* ---------- render ---------- */
  render() {
    const tiers = this.tierEls;

    tiers.forEach((t, i) => {
      const v = this.tierView(t);
      const selected = i === this.state.tierIndex;
      t.el.setAttribute('aria-pressed', selected ? 'true' : 'false');

      const hasDiscount = v.full > v.price + 0.001;
      const savePct = v.full > 0 ? Math.round((1 - v.price / v.full) * 100) : 0;

      const labelEl = t.el.querySelector('[data-label-out]');
      const perEl = t.el.querySelector('[data-per]');
      const bestEl = t.el.querySelector('[data-best]');
      const saveEl = t.el.querySelector('[data-save]');
      const anchorEl = t.el.querySelector('[data-anchor-note]');
      const priceEl = t.el.querySelector('[data-price-out]');
      const fullEl = t.el.querySelector('[data-full]');

      if (labelEl) labelEl.textContent = v.label;
      priceEl.textContent = this.fmt(v.price);

      // per-stuk badge: bij meer dan 1 stuk. In gratis-modus optioneel (toggle).
      const showPer = v.total > 1 && (this.freeMode ? this.freeShowPer : true);
      if (showPer) {
        perEl.textContent = (this.fmt(v.per) + ' ' + this.perSuffix).trim();
        perEl.hidden = false;
      } else {
        perEl.hidden = true;
      }

      // "Meest gekozen" highlight: normale modus op recommended_qty, gratis-modus op de voorselectie.
      const isBest = this.freeMode
        ? (this.showRecommended && t.slot === this.freeRecommended)
        : (this.showRecommended && this.recommendedQty && t.qty === this.recommendedQty);
      bestEl.hidden = !isBest;

      // besparing / ankerprijs. In gratis-modus is de besparingsregel optioneel (toggle).
      if (hasDiscount) {
        const showSave = this.freeMode ? this.freeShowSavings : true;
        const line = showSave ? this.savingsLine(v.save, savePct) : '';
        saveEl.textContent = line;
        saveEl.hidden = !line;
        fullEl.textContent = this.fmt(v.full);
        fullEl.hidden = false;
        anchorEl.hidden = true;
      } else {
        saveEl.hidden = true;
        fullEl.hidden = true;
        anchorEl.hidden = false;
      }
    });

    // Colors: selected state + kleurmix disable bij 1 stuk.
    const oneStuk = this.currentTotal() === 1;
    this.querySelectorAll('[data-color]').forEach((btn) => {
      if (btn.dataset.mix === 'true') {
        btn.setAttribute('aria-disabled', oneStuk ? 'true' : 'false');
        btn.title = oneStuk ? 'Kleurmix is beschikbaar vanaf 2 stuks' : this.mixLabel;
        btn.setAttribute('aria-pressed', this.state.mix && !oneStuk ? 'true' : 'false');
      } else {
        btn.setAttribute('aria-pressed', !this.state.mix && btn.dataset.value === this.state.color ? 'true' : 'false');
      }
    });

    const colorNameEl = this.querySelector('[data-color-name]');
    if (colorNameEl) colorNameEl.textContent = this.colorLabel();

    this.querySelectorAll('[data-size]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.value === this.state.size ? 'true' : 'false');
    });

    // Progress.
    const tier = this.currentTier();
    const idx = this.state.tierIndex;
    const next = tiers[idx + 1];
    const v = this.tierView(tier);
    const hasDiscount = v.full > v.price + 0.001;
    const curSave = v.save;
    const labelEl = this.querySelector('[data-progress-label]');
    const curEl = this.querySelector('[data-progress-current]');
    const barEl = this.querySelector('[data-progress-bar]');
    if (barEl) barEl.style.width = Math.round(((idx + 1) / tiers.length) * 100) + '%';

    if (this.freeMode) {
      // Hoofdtekst: per pack zelf instelbaar (eigen voortgangstekst), anders het sjabloon.
      // [aantal]/[gratis]/[bedrag] vullen we automatisch, maar je mag ook gewoon een vast
      // aantal intypen — zo bepaal je het aantal gratis boxers in de balk exact zelf.
      if (labelEl) {
        const override = (tier && tier.freeProgress) || '';
        if (next) {
          const nv = this.tierView(next);
          const tpl = override || this.freeProgressTemplate;
          labelEl.textContent = this.fillTokens(tpl, {
            aantal: nv.total - v.total,
            gratis: nv.free - v.free,
            bedrag: this.fmtProgress(Math.max(0, nv.save - v.save)),
          });
          labelEl.hidden = false;
        } else {
          const txt = override || this.freeDone;
          labelEl.textContent = txt;
          labelEl.hidden = !txt;
        }
      }
      // Eronder: totale besparing in euro's.
      if (curEl) {
        if (curSave > 0 && this.freeProgressCurrent) {
          curEl.textContent = this.fillTokens(this.freeProgressCurrent, { bedrag: this.fmtProgress(curSave) });
          curEl.hidden = false;
        } else {
          curEl.hidden = true;
        }
      }
    } else {
      if (labelEl) {
        if (next) {
          const nv = this.tierView(next);
          const diff = nv.total - v.total;
          const unit = diff === 1 ? this.progressUnit : this.progressUnitPlural;
          const nextPct = nv.full > 0 ? Math.round((1 - nv.price / nv.full) * 100) : 0;
          labelEl.textContent = this.fillTokens(this.progressTemplate, {
            aantal: diff,
            eenheid: unit,
            bedrag: this.fmtProgress(Math.max(0, nv.save - curSave)),
            besparing: this.fmtProgress(nv.save),
            korting: nextPct + '%',
            prijs: this.fmtProgress(nv.per),
          });
          labelEl.hidden = false;
        } else {
          labelEl.textContent = this.progressDone;
          labelEl.hidden = !this.progressDone;
        }
      }
      if (curEl) {
        if (hasDiscount && this.progressCurrentTemplate) {
          curEl.textContent = this.fillTokens(this.progressCurrentTemplate, { bedrag: this.fmtProgress(curSave) });
          curEl.hidden = false;
        } else {
          curEl.hidden = true;
        }
      }
    }
  }

  /* ---------- cart ---------- */
  findVariant(color, size) {
    return this.variants.find(
      (v) => v.available && v['option' + this.colorPosition] === color && v['option' + this.sizePosition] === size
    );
  }

  buildItems() {
    const size = this.state.size;
    if (!size) return { error: 'Kies eerst een maat.' };
    const total = this.currentTotal();
    if (total <= 0) return { error: 'Kies eerst een aantal.' };

    if (this.state.mix) {
      const colors = this.realColors;
      if (!colors.length) return { error: 'Geen kleuren beschikbaar.' };
      const n = colors.length;
      const base = Math.floor(total / n);
      const rem = total % n;
      const items = [];
      for (let i = 0; i < n; i++) {
        const count = base + (i < rem ? 1 : 0);
        if (count <= 0) continue;
        const variant = this.findVariant(colors[i], size);
        if (!variant) return { error: 'Deze combinatie is niet op voorraad.' };
        items.push({ id: variant.id, quantity: count });
      }
      return { items };
    }

    const variant = this.findVariant(this.state.color, size);
    if (!variant) return { error: 'Deze combinatie is niet op voorraad.' };
    return { items: [{ id: variant.id, quantity: total }] };
  }

  async addToCart() {
    if (this._busy) return;
    const result = this.buildItems();
    if (result.error) {
      this.showError(result.error);
      return;
    }
    this.setBusy(true);
    this.showError('');

    const sections = [...document.querySelectorAll('cart-items-component, cart-drawer-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: result.items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        this.showError(data.description || 'Toevoegen mislukt. Probeer opnieuw.');
        return;
      }
      await this.refreshCart(data);
    } catch (e) {
      this.showError('Er ging iets mis. Probeer opnieuw.');
    } finally {
      this.setBusy(false);
    }
  }

  async refreshCart(data) {
    try {
      const { CartAddEvent } = await import('@theme/events');
      document.dispatchEvent(
        new CartAddEvent(data, this.dataset.blockId || 'bundle-klassiek', {
          source: 'bundle-klassiek',
          sections: data.sections,
        })
      );
    } catch (_) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      setTimeout(() => drawer.open(), 100);
    }
  }

  setBusy(busy) {
    this._busy = busy;
    const cta = this.querySelector('[data-add]');
    if (!cta) return;
    cta.setAttribute('aria-busy', busy ? 'true' : 'false');
    const text = cta.querySelector('.bk__cta-text');
    const spinner = cta.querySelector('.bk__cta-spinner');
    if (text) text.style.visibility = busy ? 'hidden' : '';
    if (spinner) spinner.hidden = !busy;
  }

  showError(msg) {
    const el = this.querySelector('[data-error]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }
}

if (!customElements.get('bundle-klassiek')) {
  customElements.define('bundle-klassiek', BundelKlassiek);
}
